import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { tournamentApi } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { resolveLoginRedirect } from '../../services/roleContext'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Public accept page for parent-managed child invites (EPIC #92).
 * Token is in the query string: /managed-invite/accept?token=...
 *
 * Phase 4: real accept. The invitee sets a name + password; the backend
 * creates their child reseller account, hands over the parent-built
 * grouping(s), connects the courses, signs them in, and we redirect into
 * the reseller portal.
 */
export default function AcceptManagedInvite() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login: setAuth } = useAuth()
  const token = searchParams.get('token') || ''

  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const { t } = useLanguage()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [agreedTerms, setAgreedTerms] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('No invitation token in the link.')
      setLoading(false)
      return
    }
    tournamentApi.get(`/public/managed-invite/${token}`)
      .then(r => {
        setInvite(r.data.invite)
        // Pre-fill the name from what the inviter typed, if any.
        const parts = String(r.data.invite?.invitedName || '').trim().split(/\s+/)
        if (parts[0]) setFirstName(parts[0])
        if (parts.length > 1) setLastName(parts.slice(1).join(' '))
      })
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [token])

  async function handleSubmit(e) {
    e.preventDefault()
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (!agreedTerms) { setError(t('sp_terms_must_accept', 'You must accept the Terms of Use to continue.')); return }
    setSubmitting(true)
    setError(null)
    try {
      const { data } = await tournamentApi.post(`/public/managed-invite/${token}/accept`, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        acceptedTerms: true,
      })
      setAuth(data.user, data.token)
      const redirect = await resolveLoginRedirect()
      navigate(redirect, { replace: true })
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <Wrapper><div style={muted}>Loading invitation…</div></Wrapper>

  if (error && !invite) {
    return (
      <Wrapper>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, color: '#dc2626' }}>Invitation problem</h2>
        <p style={{ color: '#64748b' }}>{error}</p>
        <p style={{ color: '#64748b', fontSize: 13 }}>If you think this is a mistake, ask the person who sent the invite to send it again.</p>
      </Wrapper>
    )
  }

  if (!invite) return null

  const typeLabel = invite.inviteType === 'course' ? 'course partner' : 'reseller partner'

  return (
    <Wrapper>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={eyebrow}>MANAGED STOREFRONT INVITATION</div>
        <h1 style={{ margin: 0, fontSize: 24, color: 'var(--secondary-950)' }}>
          {invite.parentName} invited you
        </h1>
        <p style={{ marginTop: 12, color: '#64748b', fontSize: 14 }}>
          Set a password to run a ready-made Play and Stay storefront as their {typeLabel}.
        </p>
      </div>

      <div style={summaryBox}>
        <div style={summaryRow}><span style={summaryLabel}>Invited as</span><span>{invite.invitedEmail}</span></div>
        <div style={summaryRow}>
          <span style={summaryLabel}>Groupings</span>
          <span>{(invite.groupings || []).map(g => g.name).join(', ') || '—'}</span>
        </div>
        {invite.message && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
            {invite.message}
          </div>
        )}
      </div>

      {error && <div style={errorBox}>{error}</div>}

      <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
        <Field label="Email">
          <input type="email" value={invite.invitedEmail} readOnly style={{ ...inputStyle, background: '#f8fafc', color: '#475569' }} />
        </Field>
        <div style={{ display: 'flex', gap: 12 }}>
          <Field label="First name" style={{ flex: 1 }}>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required style={inputStyle} autoFocus />
          </Field>
          <Field label="Last name" style={{ flex: 1 }}>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required style={inputStyle} />
          </Field>
        </div>
        <Field label="Set a password" hint="At least 8 characters.">
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} style={inputStyle} placeholder="••••••••" />
        </Field>

        <label style={termsRow}>
          <input type="checkbox" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            {t('sp_terms_agree_prefix', 'I have read and agree to the')}{' '}
            <a href="/terms" target="_blank" rel="noopener" style={{ color: 'var(--brand-600)' }}>{t('sp_terms_of_use', 'Terms of Use')}</a>.
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting || !agreedTerms}
          className="btn btn-dark"
          style={{ width: '100%', marginTop: 8, padding: '14px', opacity: (submitting || !agreedTerms) ? 0.6 : 1 }}
        >
          {submitting ? 'Setting up your storefront…' : 'Accept and continue'}
        </button>
      </form>
    </Wrapper>
  )
}

function Wrapper({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff 50%, var(--brand-100) 100%)', padding: 20 }}>
      <div className="card" style={{ padding: 40, width: '100%', maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <img src="/integolf-logo.svg" alt="InteGolf" style={{ height: 40 }} />
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--secondary-800)', marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

const muted = { color: '#64748b', textAlign: 'center', padding: 20 }
const eyebrow = { fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }
const summaryBox = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, fontSize: 14 }
const summaryRow = { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }
const summaryLabel = { color: '#64748b', flexShrink: 0 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, marginTop: 16, fontSize: 13 }
const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid var(--input-border)', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit', color: 'var(--text)', boxSizing: 'border-box' }
const termsRow = { display: 'flex', alignItems: 'flex-start', gap: 10, margin: '6px 0 4px', fontSize: 13, color: 'var(--secondary-800)', lineHeight: 1.5, cursor: 'pointer' }
